import { datadogRum } from "@datadog/browser-rum";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";
import { useMutation } from "react-query";

import { classifyPhotos } from "api/classifyPhotos";
import { clientConfigAtom } from "atoms/clientConfig";
import {
  photoUploadCompleteAtom,
  photosClassifiedAtom,
  photosScannedAtom,
  uploadedPhotosAtom,
} from "atoms/photosUpload";
import { pointsOfImpactOrderedListAtom } from "atoms/pointsOfImpact";
import { sessionIdAtom } from "atoms/sessionId";
import { loaderTranslationKeyAtom } from "atoms/vehicleAnalysis";
import { mileageAtom } from "atoms/vehicleCondition/mileage";
import { vehicleConditionQuestionsAtom } from "atoms/vehicleCondition/questions";
import { vinAtom } from "atoms/vin/vin";
import { generateClassifyBody } from "utils/generateClassifyBody/generateClassifyBody";

import { clientIdAtom } from "../../../atoms/clientId";
import { LoaderTranslationKey } from "../constants";

export const useStartPhotoClassification = () => {
  const sessionId = useAtomValue(sessionIdAtom);
  const clientId = useAtomValue(clientIdAtom);
  const setLoaderMessage = useSetAtom(loaderTranslationKeyAtom);
  const [photosClassified, setPhotosClassified] = useAtom(photosClassifiedAtom);
  const [photosScanned, setPhotosScanned] = useAtom(photosScannedAtom);
  const uploadComplete = useAtomValue(photoUploadCompleteAtom);
  const uploadedPhotos = useAtomValue(uploadedPhotosAtom);
  const mileage = useAtomValue(mileageAtom);
  const vinInput = useAtomValue(vinAtom);
  const config = useAtomValue(clientConfigAtom);
  const pointsOfImpact = useAtomValue(pointsOfImpactOrderedListAtom);
  const vehicleConditionQuestions = useAtomValue(vehicleConditionQuestionsAtom);
  const { mutate: postPhotosForClassification, error: photoClassificationError } = useMutation(
    () =>
      classifyPhotos(
        sessionId,
        clientId,
        generateClassifyBody(
          {
            uploadedPhotos,
            vehicleConditionQuestions,
            pointsOfImpact,
            mileage,
            vinInput,
          },
          config
        )
      ),
    {
      onSuccess: () => {
        setPhotosClassified(true);

        /** wait 3 seconds to allow ample time for user to read ScanningPhotos message */
        setTimeout(() => setPhotosScanned(true), 3000);
      },
      onError: (error) => {
        datadogRum.addError(error, {
          message: "Failed to classify session",
        });
      },
    }
  );

  useEffect(() => {
    if (uploadComplete && !photosScanned) {
      setLoaderMessage(LoaderTranslationKey.ScanningPhotos);
    }
  }, [uploadComplete, photosScanned, setLoaderMessage]);

  useEffect(() => {
    if (uploadComplete && !photosClassified) {
      postPhotosForClassification();
    }
  }, [uploadComplete, photosClassified, postPhotosForClassification]);

  return {
    retryPhotoClassification: postPhotosForClassification,
    photoClassificationError,
    photosScanned,
  };
};
